import type { NextComponentType } from 'next'
import type { AppContext } from 'next/app'
import React, { Component } from 'react'
import type { Store } from 'redux'
import type { Epic } from 'redux-observable'

import type { ApisType, CreateApis } from '@/legacy-src/store/createApis'
import type { CreateStore, CreateStoreState } from '@/legacy-src/store/createStore'

export interface Config {
  serializeState?: (state: any) => any
  deserializeState?: (state: any) => any
  storeKey?: string
  debug?: boolean
  overrideIsServer?: boolean
}

export interface NextJSAppContext extends AppContext {
  ctx: any
}

export type CtxType = { apis: ApisType; rootEpic: Epic }

export interface InitStoreOptions {
  initialState?: CreateStoreState
  createStore: CreateStore
  config?: Config
  ctx: CtxType
}

export interface WrappedAppProps {
  initialProps: any // stuff returned from getInitialProps
  initialState: any // stuff in the Store state after getInitialProps
  isServer: boolean
}

const defaultConfig: Config = {
  storeKey: '__NEXT_REDUX_STORE__',
  debug: false,
  serializeState: (state: CreateStoreState): unknown => state,
  deserializeState: (state: unknown): CreateStoreState => state as CreateStoreState,
}

export const initStore = ({ initialState, ctx, config, createStore }: InitStoreOptions) => {
  config = {
    ...defaultConfig,
    ...config,
  }

  const isServer = typeof window === 'undefined'

  const create = () =>
    createStore({
      initialState:
        config && typeof config.deserializeState === 'function' ? config.deserializeState(initialState) : undefined,
      isServer,
      apis: ctx.apis,
      rootEpic: ctx.rootEpic,
    })

  // Memoize store if client
  if (!isServer && config && config.storeKey) {
    if (!(config.storeKey in window)) {
      window[config.storeKey] = create()
    }

    return window[config.storeKey] as ReturnType<typeof create>
  }

  return create()
}

export const withReduxAndAxios = (createStore: CreateStore, createApis: CreateApis, config?: Config) => {
  const isServer = typeof window === 'undefined'

  return (App: NextComponentType | any) =>
    class WrappedApp extends Component<WrappedAppProps> {
      store: Store
      apis: Record<string, any>

      static getInitialProps = async (appCtx: NextJSAppContext) => {
        /* istanbul ignore next */
        if (!appCtx) throw new Error('No app context')
        /* istanbul ignore next */
        if (!appCtx.ctx) throw new Error('No page context')

        const { apis, rootEpic } = createApis()
        appCtx.ctx.apis = apis
        appCtx.ctx.rootEpic = rootEpic

        const store = initStore({
          ctx: appCtx.ctx,
          createStore,
          config,
        })

        if (config && config.debug)
          console.info('1. WrappedApp.getInitialProps wrapper got the store with state', store.getState())

        appCtx.ctx.store = store
        appCtx.ctx.isServer = isServer

        let initialProps = {}

        if ('getInitialProps' in App) {
          initialProps = await App.getInitialProps.call(App, appCtx)
        }

        if (config && config.debug) console.info('3. WrappedApp.getInitialProps has store state', store.getState())

        return {
          isServer,
          initialState: isServer ? config?.serializeState?.(store.getState()) : store.getState(),
          initialProps,
        }
      }

      static displayName = `withRedux(${App.displayName || App.name || 'App'})`

      constructor(props, context: any) {
        super(props, context)

        const { initialState } = props

        if (config && config.debug)
          console.info('4. WrappedApp.render created new store with initialState', initialState, isServer)

        const { apis, rootEpic } = createApis()
        this.apis = apis

        this.store = initStore({
          initialState,
          ctx: { rootEpic, apis },
          createStore,
          config,
        })

        this.apis.connect(this.store.getState, this.store.dispatch)
      }

      render() {
        const { initialProps, ...props } = this.props

        // Cmp render must return something like <Provider><Component/></Provider>
        return <App {...props} {...initialProps} apis={this.apis} setToken={this.apis.setToken} store={this.store} />
      }
    }
}
